import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { useRoleContext } from "../../context/RoleContext";
import { useAuthUser } from "../../hooks/AuthProvider";
import { getPropertyById, getActiveLeaseByProperty, getMyPropertyTickets } from "../../helpers/firestoreService";

const TenantDashboard = () => {
  document.title = "My Dashboard | Porcher";

  const { activeTenant } = useRoleContext();
  const user = useAuthUser();

  const [property, setProperty] = useState(null);
  const [lease, setLease] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeTenant) return;
    const load = async () => {
      setLoading(true);
      try {
        const [prop, activeLease] = await Promise.all([
          getPropertyById(activeTenant.propertyId),
          getActiveLeaseByProperty(activeTenant.propertyId),
        ]);
        setProperty(prop);
        setLease(activeLease);
        setTickets(user?.uid ? await getMyPropertyTickets(user.uid) : []);
      } catch (e) {
        console.error("TenantDashboard load error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeTenant, user]);

  if (!activeTenant) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5 text-muted">
            <i className="bx bx-user-x font-size-40 d-block mb-3" />
            <h5>No tenant record linked to your account</h5>
            <p className="small">Accept a lease invitation from your property manager to get started.</p>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 120 }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h4 className="mb-1 text-white">Welcome back, {activeTenant.name || user?.displayName || "Tenant"}</h4>
            <p className="mb-0 opacity-75">
              <i className="bx bx-map-pin me-1" />
              {property?.address || "Loading address…"}
            </p>
          </div>
        </div>

        <Row className="g-3">
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{ width: 44, height: 44, background: "#556ee618" }}>
                    <i className="bx bx-file fs-4 text-primary" />
                  </div>
                  <h6 className="mb-0">Active Lease</h6>
                </div>
                {lease ? (
                  <>
                    <p className="mb-1 small text-muted">
                      {lease.startDate} → {lease.endDate}
                    </p>
                    <p className="mb-2 fw-semibold">${lease.monthlyRent?.toLocaleString()}/mo</p>
                    <Link to="/tenant/my-rentals" className="btn btn-sm btn-outline-primary">My Rentals</Link>
                  </>
                ) : (
                  <p className="text-muted small mb-0">No active lease found.</p>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{ width: 44, height: 44, background: "#34c38f18" }}>
                    <i className="bx bx-building-house fs-4 text-success" />
                  </div>
                  <h6 className="mb-0">My Property</h6>
                </div>
                {property ? (
                  <>
                    <p className="mb-2 small text-muted">{property.address}</p>
                    <Link to="/tenant/property" className="btn btn-sm btn-outline-success">View Details</Link>
                  </>
                ) : (
                  <p className="text-muted small mb-0">No property found.</p>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{ width: 44, height: 44, background: "#f1b44c18" }}>
                    <i className="bx bx-task fs-4 text-warning" />
                  </div>
                  <h6 className="mb-0">My Tickets</h6>
                </div>
                <p className="mb-2 small text-muted">{tickets.length} recent ticket{tickets.length !== 1 ? "s" : ""}</p>
                <Link to="/tenant/tickets" className="btn btn-sm btn-outline-warning">View Tickets</Link>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TenantDashboard;
